import {$csI18n, numberFormatCode, datepickerLocaleCode, syncfusionLocaleCode, getDayJsByLocale} from '../modules/csI18n';
import {Day} from '@/lc-datepicker/src/lc-datepicker/libs/bootstrap';

export default {
	install: function(Vue) {
		/**
		 * @public
		 * @description
		 * Helper method to access the global $csI18n instance
		 */
		Vue.prototype.$globalI18n = function() {
			return $csI18n;
		}
		/**
		 * @public
		 * @description
		 * Converts an array of translations in to the appropriate format and passes them in to the translation plugin
		 * 
		 * @param {Array} translations An array of translations to look through
		 * @param {String} suffix A suffix to add to the translation key for uniqueness
		 */
		Vue.prototype.$transformAndAddTranslations = function(translations, suffix) {
			if (!this.$isMultiLingual()) return;
			for (let l = 0, language; (language = translations[l]) !== undefined; l++) {
				if (Array.isArray(language.translationTexts)) {
					for (let t = 0, translation; translation = language.translationTexts[t]; t++) {
						this.$i18n.mergeLocaleMessage(language.locale, { [translation.key + suffix] : translation.message });
					}
				}
			}
		}

		/**
		 * @public
		 * @description
		 * Adds any messages found in screen configurations to the list of translations
		 * 
		 * @param {Array} translationsGroup The group to search for nested translations within
		 * @param {String} groupKey The translation key to record the main group under
		 * @param {String} groupIndex The index of an item in the translation group to use as a unique id for each item
		 * @param {String} defaultMessageKey The default key to assign to the default translation message
		 * @param {Object} existingTranslations The object containing the translation groups, keyed by locale
		 * @param {Boolean} isRecursing Whether the function is a recursive layer down
		 * @param {String} recursionKey The groupItem key to search for to determine if recursion is necessary
		 * @param {Object} additionalGroupProperties Any additional properties to add to the group while looping
		 * 
		 */
		Vue.prototype.$addGroupedTranslations = function(translationsGroup, groupKey, groupIndex, defaultMessageKey, existingTranslations, isRecursing, recursionKey, additionalGroupProperties) {
			if (!this.$isMultiLingual()) return;
			for (let g = 0, groupItem; groupItem = translationsGroup[g]; g++) {
				if (additionalGroupProperties && Object.keys(additionalGroupProperties).length > 0) {
					for (let prop in additionalGroupProperties) {
						groupItem[prop] = additionalGroupProperties[prop];
					}
				}

				if (groupItem.translations) {
					for (let l = 0, language; language = groupItem.translations[l]; l++) {
						if (!existingTranslations[language.locale]) {
							existingTranslations[language.locale] = {};
						}

						for (let t = 0, translation; translation = language.translationTexts[t]; t++) {
							let itemIndex = (groupIndex) ? groupItem[groupIndex] : g;
							if (!existingTranslations[language.locale][itemIndex]) {
								existingTranslations[language.locale][itemIndex] = {
									[translation.key] : translation.message
								}
							} else {
								existingTranslations[language.locale][itemIndex][translation.key] = translation.message;
							}
						}
					}
				} else {
					if (defaultMessageKey && !existingTranslations.en) {
						let itemIndex = (groupIndex) ? groupItem[groupIndex] : g;
						existingTranslations.en = {
							[itemIndex] : {
								[defaultMessageKey] : groupItem.title
							}
						};
					}
				}

				if (recursionKey && groupItem[recursionKey]) {
					this.$addGroupedTranslations(groupItem[recursionKey], groupKey, groupIndex, defaultMessageKey, existingTranslations, true, recursionKey);
				}
			}

			if (!isRecursing) {
				for (let locale in existingTranslations) {
					let localMessages = existingTranslations[locale];
					this.$i18n.mergeLocaleMessage(locale, { [groupKey] : localMessages });
				}
			}
		}
		
		/**
         * @public
         * @description
         * Translates an item if multilingual is on, or returns the default version if not
         *
         * @param {String} translationPath The path to the translation key
         * @param {String} defaultValue The value to return if multilingual is off
         *
         * @returns {String}
         */
		 Vue.prototype.$translateWithDefault = function(translationPath, defaultValue) { 
			if (!this.$isMultiLingual() && defaultValue) return defaultValue;
            this.$cs_.timerStart('translateWithDefault');
            let result = defaultValue;
            if($csI18n.te(translationPath)) {
				result = $csI18n.t(translationPath);
			} else {
				if (!defaultValue) {
					// using to help debug during the dev process
                	//console.warn(`${translationPath} not found in global locale messages, using ${defaultValue} as the default value`);
				} else {
					//console.warn(`${translationPath} -> ${defaultValue} as the default value`);
				}
            }

            this.$cs_.timerAdd('translateWithDefault');
            return result;
        }
		
		/**
         * @public
         * @description
         * Translates an item if multilingual is on, or returns the default version if not
		 * THIS IS FOR ITEMS TO BE USED AT THE COMPONENT LEVEL ONLY!
         *
         * @param {String} translationPath The path to the translation key
         * @param {String} defaultValue The value to return if multilingual is off
         *
         * @returns {String}
         */
		Vue.prototype.$translateWithDefaultLocalScope = function(translationPath, defaultValue) {
			if (!this.$isMultiLingual() && defaultValue) return defaultValue;
            this.$cs_.timerStart('translateWithDefaultLocalScope');
            let result = defaultValue;
            if (this.$isMultiLingual() && this.$te(translationPath)) {
                result = this.$t(translationPath);
            } else {
				if (!defaultValue) {
					// using to help debug during the dev process
                	// console.warn(`${translationPath} not found in LOCAL SCOPE locale messages, using ${defaultValue} as the default value`);
				} else {
					//console.warn(`${translationPath} -> ${defaultValue} as the default value`);
				}
            }
			this.$cs_.timerAdd('translateWithDefaultLocalScope');
            return result;
        }

		/**
         * @public
         * @description
         * Do we have the target value stored in locale locally?
		 * THIS IS FOR ITEMS TO BE USED AT THE COMPONENT LEVEL ONLY!
         *
         * @param {String} translationPath The path to the translation key
         *
         * @returns {String}
         */
		 Vue.prototype.$localTranslationKeyExists = function(translationPath) {
            return this.$isMultiLingual() && this.$te(translationPath);
        }
		
		Vue.prototype.$getAppLocale = function() {
			return $csI18n.locale;
		}

		/**
         * @public
         * @description
         * To use the hello world example, we can translate this: 
		 * { hello: '{0} world' } and it call it by $csI18n.t('hello', ['hello']
         *
         * @param {String} translationPath The path to the translation key
         * @param {Array} args an array of values to fill the placeholder
         *
         * @returns {String}
         */
		 Vue.prototype.$translateWithArgs = function(translationPath, args) {
            this.$cs_.timerStart('translateWithArgs');

            // this.$isMultiLingual() && 
            if ($csI18n.te(translationPath)) {
                return $csI18n.t(translationPath, args);
            } else {
                console.error(`${translationPath} not found in locale messages (WITH ARGS)`)
            }

            this.$cs_.timerAdd('translateWithArgs');
            return '';
        }

		/**
		 * @public
		 * @description
		 * Added this method as the one above didn't seem to work correctly with our locale.
		 * This will return the right locale value for a passed in number
		   @param {integer} number
		 * @returns {string}
		 */
		Vue.prototype.$numberFromLocale = function(number) {
			if ($csI18n.locale === 'en') {
				return number;
			}
			//if we get complaints over comments in the number string, we can use `.replace(/,/g,"")` on the below
			return new Intl.NumberFormat(numberFormatCode()).format(number);
		};

		/**
		 * @public
		 * @description
		 * Return the string of the locale being used in the datepicker
		 * @returns {string}
		 */
		Vue.prototype.$datepickerLocaleCode = function() {
			return datepickerLocaleCode();
		}

		/**
		 * @public
		 * @description
		 * Return the string of the locale being used in syncfusion
		 * @returns {string}
		 */
		Vue.prototype.$syncfusionLocaleCode = function() {
			return syncfusionLocaleCode();
		}

		/**
		 * @public
		 * @description
		 * Return the string of the locale being used in agGrid
		 * @returns {string}
		 */
		Vue.prototype.$agGridLocaleJson = function() {
			return $csI18n.t('agGrid');
		}

		/**
		 * @public
		 * @description
		 * Return the string of the locale being used in dayjs
		 * @returns {string}
		 */
		Vue.prototype.$dayJsByLocale = function() {
			return getDayJsByLocale();
		}
		
		/**
		 * @public
		 * @description
		 * Receive a date string and return it formatted by locale
		 * @param {String} dateString 
		 * @param {Boolean} includeTime 
		 * @returns {string}
		 */
		Vue.prototype.$getLocalisedDate = function(dateString, includeTime = false) {
			const date = Day.getDayJsDateObject(new Date(dateString), {locale: this.$datepickerLocaleCode()});
			if (this.$datepickerLocaleCode() == 'en') return date.format(this.$dateOutputFormat(includeTime));
			return date.format('LLLL');
		}
	}
}
